import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";

// Types
export type Point = { x: number; y: number };
export type Stroke = { points: Point[] };

export type SignaturePadHandle = {
  clear: () => void;
  undo: () => void;
  isEmpty: () => boolean;
  toDataURL: (type?: string, quality?: number) => string;
  toBlob: (type?: string, quality?: number) => Promise<Blob>;
  loadFromDataURL: (dataUrl: string) => Promise<void>;
};

export type SignaturePadProps = {
  className?: string;
  lineWidth?: number; // base line width (px)
  strokeStyle?: string; // CSS color
  backgroundColor?: string | null; // when exporting, fill bg if provided
  height?: number; // CSS height
  // If width is not provided, pad is responsive to container width
  width?: number; // CSS width
  onChange?: (isEmpty: boolean) => void;
};

// Utility: device-pixel-ratio canvas scaler
function fitCanvasToDisplaySize(canvas: HTMLCanvasElement) {
  const dpr = Math.max(1, window.devicePixelRatio || 1);
  const rect = canvas.getBoundingClientRect();
  const cssWidth = Math.max(1, Math.floor(rect.width));
  const cssHeight = Math.max(1, Math.floor(rect.height));
  const neededWidth = Math.floor(cssWidth * dpr);
  const neededHeight = Math.floor(cssHeight * dpr);

  if (canvas.width !== neededWidth || canvas.height !== neededHeight) {
    canvas.width = neededWidth;
    canvas.height = neededHeight;
    const ctx = canvas.getContext("2d")!;
    ctx.scale(dpr, dpr);
    return true;
  }
  return false;
}

function drawStroke(ctx: CanvasRenderingContext2D, stroke: Stroke, lineWidth: number, color: string) {
  if (!stroke.points.length) return;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  const [first, ...rest] = stroke.points;
  ctx.moveTo(first.x, first.y);
  for (const p of rest) ctx.lineTo(p.x, p.y);
  ctx.stroke();
}

function redrawAll(ctx: CanvasRenderingContext2D, strokes: Stroke[], lw: number, color: string) {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  // Because ctx is scaled for DPR, clearRect uses device pixels; we want CSS px clear as well
  // but since we scaled the context, clearing device pixels is fine.
  for (const s of strokes) drawStroke(ctx, s, lw, color);
}

const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(function SignaturePad(
  { className = "", lineWidth = 2.4, strokeStyle = "#111827", backgroundColor = null, height = 240, width, onChange },
  ref
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const currentStrokeRef = useRef<Stroke>({ points: [] });
  const [empty, setEmpty] = useState(true);

  // Setup canvas context and resize handling
  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctxRef.current = ctx;

    const resize = () => {
      if (!canvas) return;
      const changed = fitCanvasToDisplaySize(canvas);
      if (changed) redrawAll(ctx, strokes, lineWidth, strokeStyle);
    };

    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    window.addEventListener("orientationchange", resize);

    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", resize);
    };
  }, [lineWidth, strokeStyle, strokes.length]);

  // Update empty state
  useEffect(() => {
    const nowEmpty = strokes.length === 0 || strokes.every(s => s.points.length === 0);
    setEmpty(nowEmpty);
    onChange?.(nowEmpty);
  }, [strokes, onChange]);

  // Helpers to get pointer position in canvas CSS pixels
  const getPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = (e.target as HTMLCanvasElement).getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    setIsDrawing(true);
    currentStrokeRef.current = { points: [getPos(e)] };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const p = getPos(e);
    const curr = currentStrokeRef.current;
    curr.points.push(p);
    const ctx = ctxRef.current;
    if (!ctx) return;
    // draw incrementally for responsiveness
    const lastTwo = curr.points.slice(-2);
    if (lastTwo.length === 2) {
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = strokeStyle;
      ctx.lineWidth = lineWidth;
      ctx.beginPath();
      ctx.moveTo(lastTwo[0].x, lastTwo[0].y);
      ctx.lineTo(lastTwo[1].x, lastTwo[1].y);
      ctx.stroke();
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    (e.target as HTMLCanvasElement).releasePointerCapture(e.pointerId);
    if (!isDrawing) return;
    setIsDrawing(false);
    const finished = currentStrokeRef.current;
    if (finished.points.length > 0) {
      setStrokes(prev => [...prev, { points: finished.points.slice() }]);
      currentStrokeRef.current = { points: [] };
    }
  };

  const clear = () => {
    setStrokes([]);
    const ctx = ctxRef.current;
    if (ctx) ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  };

  const undo = () => {
    setStrokes(prev => {
      const next = prev.slice(0, -1);
      const ctx = ctxRef.current;
      if (ctx) redrawAll(ctx, next, lineWidth, strokeStyle);
      return next;
    });
  };

  const isEmpty = () => empty;

  const toDataURL = (type = "image/png", quality?: number) => {
    const canvas = canvasRef.current!;
    if (!backgroundColor) return canvas.toDataURL(type, quality);
    // draw to an export canvas with background color
    const exportCanvas = document.createElement("canvas");
    const rect = canvas.getBoundingClientRect();
    exportCanvas.width = Math.max(1, Math.floor(rect.width));
    exportCanvas.height = Math.max(1, Math.floor(rect.height));
    const exportCtx = exportCanvas.getContext("2d")!;
    exportCtx.fillStyle = backgroundColor;
    exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    // redraw strokes
    exportCtx.lineCap = "round";
    exportCtx.lineJoin = "round";
    for (const s of strokes) drawStroke(exportCtx as any, s, lineWidth, strokeStyle);
    return exportCanvas.toDataURL(type, quality);
  };

  const toBlob = (type = "image/png", quality?: number) => {
    return new Promise<Blob>((resolve) => {
      const dataUrl = toDataURL(type, quality);
      fetch(dataUrl).then(r => r.blob()).then(resolve);
    });
  };

  const loadFromDataURL = async (dataUrl: string) => {
    // Just draw the image on canvas and reset strokes (cannot recover vector strokes from PNG)
    const img = new Image();
    img.src = dataUrl;
    await img.decode();
    const ctx = ctxRef.current;
    if (!ctx) return;
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    // Draw scaled to canvas CSS size
    const rect = ctx.canvas.getBoundingClientRect();
    ctx.drawImage(img, 0, 0, rect.width, rect.height);
    setStrokes([]); // bitmap loaded, undo history cleared
  };

  useImperativeHandle(ref, () => ({ clear, undo, isEmpty, toDataURL, toBlob, loadFromDataURL }), [empty, strokes, lineWidth, strokeStyle]);

  return (
    <div ref={containerRef}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex gap-2">
          <button type="button" onClick={undo} className="ontario-button ontario-button--primary">Undo</button>
          <button type="button" onClick={clear} className="ontario-button ontario-button--secondary">Clear</button>
        </div>
      </div>
      <div>
        <canvas className="ontario-input ontario-textarea"
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          style={{ width: width ? `${width}px` : "100%", height: `${height}px`, touchAction: "none", display: "block" }}
        />
      </div>
      <div>Tip: Use your mouse, trackpad, or finger on touch screens.</div>
    </div>
  );
});

export default function SignaturePadComponent() {
  const padRef = useRef<SignaturePadHandle | null>(null);
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  const handleSavePng = async () => {
    const url = padRef.current?.toDataURL("image/png");
    if (url) setDataUrl(url);
  };

  const handleDownload = async () => {
    const blob = await padRef.current!.toBlob("image/png");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "signature.png";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div>
      <div className="max-w-2xl mx-auto">
        <SignaturePad ref={padRef} height={260} lineWidth={2.6} strokeStyle="#0f172a" backgroundColor="#ffffff" />

        <div className="mt-4 flex gap-2">
          <button onClick={handleSavePng} className="ontario-button ontario-button--primary">Preview Signiture</button>
          <button onClick={handleDownload} className="ontario-button ontario-button--secondary">Download Signiture</button>
        </div>

        {dataUrl && (
          <div className="mt-4">
            <div>Preview:</div>
            <img src={dataUrl} alt="signature preview" />
          </div>
        )}
      </div>
    </div>
  );
}
